import { useCallback, useMemo } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { MarketoBillLeadInput } from '_gql'

import { useIntl } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import { getAccountingSoftwareItem } from '@sputnik/components/AccountingSoftwareDropdown'
import { getE164FormattedPhoneNumber } from '@sputnik/components/utils'
import { getMarketoValues } from '@sputnik/marketo'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { autoInitCreditApplication, type InitCreditApplication } from '@sputnik/schema/creditApplication/init'
import { ApplicantData, useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'
import { getClientTimezone } from '@sputnik/utils/timezoneUtil'

import {
  InitCreditApplicationV3_initCreditApplication,
  InitCreditApplicationV3_initCreditApplication_CreditApplication,
  InitCreditApplicationV3Variables,
  useInitCreditApplicationV3,
} from './InitCreditApplicationV3.gql'

const isResponseCreditApplicationType = (
  response: InitCreditApplicationV3_initCreditApplication,
): response is InitCreditApplicationV3_initCreditApplication_CreditApplication => {
  return response.__typename === 'CreditApplication'
}

const convertApplicantDataToInitCreditApplicationData = (applicantData: ApplicantData | null) => {
  if (!applicantData) {
    return null
  }

  const {
    accountingSoftware,
    companyName,
    email,
    firstName,
    lastName,
    legalBusinessName,
    numberOfEmployees,
    phone,
    title,
  } = applicantData

  return {
    email: email ?? '',
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    phoneNumber: getE164FormattedPhoneNumber(undefined, phone ?? '') ?? '',
    legalBusinessName: legalBusinessName ?? companyName ?? '',
    accountingSoftware: accountingSoftware ?? '',
    numberOfEmployees: numberOfEmployees ? Number(numberOfEmployees) : 0,
    title: title ?? '',
  }
}

const useInitCreditApplication = () => {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const { requestApplicantToken } = useAuth()
  const showToast = useToast()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const { track, applicantData, dataFromUp } = useWebTracking()

  const [initCreditAppMutation, { loading: isLoadingInitCreditApplication }] = useInitCreditApplicationV3({
    onCompleted: (data) => {
      if (isResponseCreditApplicationType(data.initCreditApplication)) {
        const { salesforceCreditId } = data.initCreditApplication ?? {}

        logInfo('initCreditApplication', {
          attributes: {
            creditAppId: salesforceCreditId,
            result: `Credit application initialized from ${pathname}`,
          },
          eventName: 'initCreditApplicationSuccess',
        })
        track('se_signup__has__started')
        const handleInitCreditAppComplete = async (salesforceCreditId: string) => {
          await requestApplicantToken(salesforceCreditId)
          navigate(
            {
              pathname: `/app/${salesforceCreditId}/${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`,
            },
            { replace: true },
          )
        }
        handleInitCreditAppComplete(salesforceCreditId)
      }
    },
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        formatMessage({
          defaultMessage: 'Error while initializing credit application.',
          id: 'sputnik.useGetQueryParamsForCreditInit__V2ViaC',
        }),
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'initCreditApplication',
          result: error,
          message: 'Error while initializing credit application.',
        },
        eventName: 'initCreditApplicationError',
      })
    },
  })

  const { executeRecaptcha } = useGoogleReCaptcha()

  const verifyReCaptcha = useCallback(
    async (action: string) => {
      if (!executeRecaptcha || !window.__DivvyEnvironment.GOOGLE_RECAPTCHA_SITE_KEY) {
        return ''
      }

      const token = await executeRecaptcha(action)

      logInfo(action, {
        attributes: {
          result: 'Token retrieved',
        },
        eventName: 'ReCaptcha',
      })

      return token
    },
    [executeRecaptcha],
  )

  const handleInitCreditApplication = useCallback(
    async (values?: InitCreditApplication) => {
      let applicationInitData: InitCreditApplication | null = values ?? null
      if (!applicationInitData && applicantData) {
        applicationInitData = convertApplicantDataToInitCreditApplicationData(applicantData)

        if (!autoInitCreditApplication.isValidSync(applicationInitData)) {
          return
        }
      }

      if (!applicationInitData) {
        return
      }

      const billMarketoLeadParams = {
        // we strip values off of what our generated type defines,
        // so casting this kind of makes sense to avoid breaking our lead tracking
        // functionality.
        marketoBillLeadInput: getMarketoValues(dataFromUp) as MarketoBillLeadInput,
      }

      const leadParams = billMarketoLeadParams?.marketoBillLeadInput
      const pact = leadParams?.pact
      const affiliateIdString = leadParams?.ltAffiliateId
      const affiliateId = (affiliateIdString && Number.parseInt(affiliateIdString)) || null
      const afidField = affiliateId ? { affiliateId } : {}

      const partnerCode = searchParams.get('partner_code') || pact || ''
      const pactField = partnerCode ? { partnerCode } : {}

      const {
        email,
        firstName,
        lastName,
        legalBusinessName,
        numberOfEmployees,
        phoneNumber,
        accountingSoftware: accountingSoftwareValue,
        title,
      } = applicationInitData
      const recaptchaToken = await verifyReCaptcha('initApplication')

      const mutationVariables: InitCreditApplicationV3Variables = {
        applicant: {
          email,
          firstName,
          lastName,
          phoneNumber,
          title: title || '',
        },
        businessInfo: {
          legalBusinessName,
          numberOfEmployees: Number(numberOfEmployees),
          accountingSoftware: getAccountingSoftwareItem(accountingSoftwareValue ?? ''),
        },
        recaptchaToken,
        ...afidField,
        ...pactField,
        ...billMarketoLeadParams,
        appVersion: 3,
        clientTimeZone: getClientTimezone() as string,
      }
      initCreditAppMutation({ variables: mutationVariables })
    },
    [applicantData, dataFromUp, initCreditAppMutation, searchParams, verifyReCaptcha],
  )

  const shouldNavigateToGetToKnowYou = useMemo(() => {
    const convertedData = convertApplicantDataToInitCreditApplicationData(applicantData)
    const isAutoInitDataValid = autoInitCreditApplication.isValidSync(convertedData)
    return !isAutoInitDataValid
  }, [applicantData])

  return {
    shouldNavigateToGetToKnowYou,
    handleInitCreditApplication,
    isLoadingInitCreditApplication,
  }
}

export default useInitCreditApplication
